/**
 * A correlação cruzada de duas séries de leituras — qc × fs, para sugerir o deslocamento de
 * profundidade do fs (spec 07 §5.4; a folha 1 do relatório do CPeT-IT) —; RN-252.
 *
 * Para cada defasagem k, de −máx a +máx leituras, o r de Pearson entre a[i] e b[i + k] nas leituras
 * em que as duas existem; a defasagem sugerida é a de maior r (no empate, a de menor |k|) entre as
 * ELEGÍVEIS: as que têm pares suficientes — pelo menos 10, e pelo menos metade das leituras válidas
 * da série mais curta. Nas pontas da curva a sobreposição encolhe, e um r com poucos pares é acaso
 * (três ou quatro pares quaisquer dão r perto de ±1): ele fica na curva, para o gráfico, com os
 * pares contados, mas não é sugerido.
 *
 * O sinal: k > 0 quer dizer que b vem k leituras DEPOIS de a (b[i + k] acompanha a[i]: b atrasado);
 * k < 0, adiantado. O fs, medido na luva acima da ponta, chega atrasado em relação ao qc: para
 * alinhá-lo, o fs sobe k leituras — no `deslocamento` da edição (`edicao.ts`, positivo = o canal
 * desce), −k vezes o passo das leituras, em cm. A edição continua do engenheiro: isto só sugere.
 *
 * Pearson é a fórmula; o que não existe — menos de três pares, série constante — é NaN, e nada
 * lança. «Constante» é também a série que só varia no ruído do ponto flutuante (0,1 repetido tem
 * desvios de 10⁻¹⁷ em torno da média): o desvio padrão tem de passar de 10⁻⁹ do maior |valor|, senão
 * o r é ruído, e sai NaN em toda defasagem. A defasagem máxima não passa do tamanho da série.
 */
import type { Serie } from './tipos';

/** Um ponto da curva: a defasagem, o r dela e os pares em que ele foi medido. */
export interface PontoDaCorrelacao {
  defasagem: number;
  /** O r de Pearson (NaN com menos de três pares ou série constante). */
  r: number;
  /** Os pares com número dos dois lados nesta defasagem. */
  pares: number;
  /** Tem pares suficientes (`paresMinimos`) para ser sugerida. */
  elegivel: boolean;
}

/** O resultado da correlação cruzada. */
export interface CorrelacaoCruzada {
  /** A defasagem elegível de maior r, em leituras (NaN se nenhuma elegível tem r). */
  defasagem: number;
  /** O r de Pearson nessa defasagem (NaN se nenhuma elegível tem r). */
  r: number;
  /** O r de cada defasagem, de −máx a +máx, elegível ou não. */
  curva: PontoDaCorrelacao[];
  /** Os pares mínimos para uma defasagem ser elegível: máx(10, metade das válidas da série mais curta). */
  paresMinimos: number;
}

/** Pares mínimos para que o r de Pearson exista (com dois, é sempre ±1). */
const PARES_MINIMOS = 3;

/** Pares mínimos para que uma defasagem possa ser sugerida, em qualquer série… */
const PARES_MINIMOS_PARA_SUGERIR = 10;

/** …e a fração das leituras válidas da série mais curta que a sobreposição tem de cobrir. */
const SOBREPOSICAO_MINIMA = 0.5;

/**
 * Abaixo deste desvio padrão relativo ao maior |valor| a série é constante: o que sobra é ruído do
 * ponto flutuante (~10⁻¹⁶), e qualquer medida de verdade varia muito mais (o qc anda em 0,01 MPa).
 */
const VARIACAO_MINIMA = 1e-9;

/** Quantas leituras da série são números finitos. */
function validas(s: Serie): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) if (Number.isFinite(s[i])) n++;
  return n;
}

/** O r de Pearson entre a[i] e b[i + k], nos pares em que os dois são números, e quantos pares. */
function pearson(a: Serie, b: Serie, k: number): { r: number; pares: number } {
  const inicio = Math.max(0, -k);
  const fim = Math.min(a.length, b.length - k); // exclusivo
  let n = 0;
  let ma = 0;
  let mb = 0;
  let maiorA = 0;
  let maiorB = 0;
  for (let i = inicio; i < fim; i++) {
    if (!Number.isFinite(a[i]) || !Number.isFinite(b[i + k])) continue;
    n++;
    ma += a[i];
    mb += b[i + k];
    maiorA = Math.max(maiorA, Math.abs(a[i]));
    maiorB = Math.max(maiorB, Math.abs(b[i + k]));
  }
  if (n < PARES_MINIMOS) return { r: NaN, pares: n };
  ma /= n;
  mb /= n;
  let saa = 0;
  let sbb = 0;
  let sab = 0;
  for (let i = inicio; i < fim; i++) {
    if (!Number.isFinite(a[i]) || !Number.isFinite(b[i + k])) continue;
    const da = a[i] - ma;
    const db = b[i + k] - mb;
    saa += da * da;
    sbb += db * db;
    sab += da * db;
  }
  // Desvio padrão (√(s/n)) acima de 10⁻⁹ do maior |valor|; a série toda zero também é constante.
  if (!(saa > n * (VARIACAO_MINIMA * maiorA) ** 2 && saa > 0)) return { r: NaN, pares: n };
  if (!(sbb > n * (VARIACAO_MINIMA * maiorB) ** 2 && sbb > 0)) return { r: NaN, pares: n };
  const r = sab / Math.sqrt(saa * sbb);
  return { r: Number.isFinite(r) ? Math.max(-1, Math.min(1, r)) : NaN, pares: n };
}

/**
 * A correlação cruzada de `a` e `b`, de −`maxDefasagem` a +`maxDefasagem` leituras (inteiro; o que
 * não for número positivo vale 0, e mais que a série maior − 1 vale isso: além dela não há par).
 * Devolve a defasagem elegível de maior r, o r dela, a curva inteira e os pares mínimos.
 */
export function correlacaoCruzada(a: Serie, b: Serie, maxDefasagem: number): CorrelacaoCruzada {
  const pedido = Number.isFinite(maxDefasagem) && maxDefasagem > 0 ? Math.floor(maxDefasagem) : 0;
  const maximo = Math.min(pedido, Math.max(0, Math.max(a.length, b.length) - 1));
  const paresMinimos = Math.max(
    PARES_MINIMOS_PARA_SUGERIR,
    Math.ceil(SOBREPOSICAO_MINIMA * Math.min(validas(a), validas(b))),
  );
  const curva: PontoDaCorrelacao[] = [];
  for (let k = 0 - maximo; k <= maximo; k++) {
    const { r, pares } = pearson(a, b, k);
    curva.push({ defasagem: k, r, pares, elegivel: pares >= paresMinimos });
  }
  let melhor: PontoDaCorrelacao | null = null;
  for (const c of curva) {
    if (!c.elegivel || Number.isNaN(c.r)) continue;
    if (melhor === null || c.r > melhor.r || (c.r === melhor.r && Math.abs(c.defasagem) < Math.abs(melhor.defasagem))) {
      melhor = c;
    }
  }
  return { defasagem: melhor ? melhor.defasagem : NaN, r: melhor ? melhor.r : NaN, curva, paresMinimos };
}
